/**
 * Print Settings Page
 * Customize receipt printing format and test print
 * Settings are synced with branch data in backend
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { thermalPrinter } from '../../services/thermalPrinterService';
import { bluetoothPrinter } from '../../services/bluetoothPrinterService';
import api from '../../services/api';

interface PrintSettings {
  paperWidth: 58 | 80;
  headerText: string;
  footerText: string;
  fontSize: 'small' | 'medium' | 'large';
  showLogo: boolean;
  compactMode: boolean;
  continuousMode: boolean; // If true, no auto-cut (continuous roll paper)
  logoBase64?: string; // Base64 encoded logo image
}

const PrintSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [settings, setSettings] = useState<PrintSettings>({
    paperWidth: 80,
    headerText: 'PhoneLabs\nSparepart & Accessories',
    footerText: 'Terima Kasih\nBarang yang sudah dibeli\ntidak dapat ditukar/dikembalikan',
    fontSize: 'medium',
    showLogo: false,
    compactMode: false,
    continuousMode: true, // Default to continuous mode (no auto-cut)
  });

  const [printerConnected, setPrinterConnected] = useState(false);
  const [printerType, setPrinterType] = useState<'usb' | 'bluetooth' | null>(null);
  const [testPrinting, setTestPrinting] = useState(false);
  const [logoError, setLogoError] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branchId, setBranchId] = useState<number | null>(null);

  // Load settings from backend (branch settings)
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const response = await api.get('/branches');
        const branches = response.data;
        
        // Get user's branch or first active branch
        const userBranch = currentUser?.branch_id 
          ? branches.find((b: any) => b.id === currentUser.branch_id)
          : branches.find((b: any) => b.is_active);
        
        if (userBranch) {
          setBranchId(userBranch.id);
          
          setSettings({
            paperWidth: userBranch.printer_paper_width || 80,
            headerText: userBranch.printer_header || 'PhoneLabs\nSparepart & Accessories',
            footerText: userBranch.printer_footer || 'Terima Kasih\nBarang yang sudah dibeli\ntidak dapat ditukar/dikembalikan',
            fontSize: 'medium',
            showLogo: !!userBranch.logo_base64,
            logoBase64: userBranch.logo_base64,
            compactMode: false,
          });
        }
      } catch (error) {
        console.error('Failed to load branch settings:', error);
        alert('⚠️ Gagal memuat pengaturan branch');
      } finally {
        setLoading(false);
      }
    };
    
    loadSettings();
  }, [currentUser]);

  // Check printer connection
  useEffect(() => {
    const checkConnection = async () => {
      // Check if printer is already connected
      const usbConnected = thermalPrinter.isConnected();
      const btConnected = bluetoothPrinter.isConnected();
      
      if (usbConnected) {
        setPrinterConnected(true);
        setPrinterType('usb');
      } else if (btConnected) {
        setPrinterConnected(true);
        setPrinterType('bluetooth');
      }
    };
    
    checkConnection();
  }, []);

  const handleSave = async () => {
    if (!branchId) {
      alert('❌ Branch tidak ditemukan');
      return;
    }

    try {
      setSaving(true);
      
      await api.patch(`/branches/${branchId}`, {
        printer_paper_width: settings.paperWidth,
        printer_header: settings.headerText,
        printer_footer: settings.footerText,
        logo_base64: settings.logoBase64 || null,
      });
      
      alert('✅ Pengaturan print tersimpan ke database!');
    } catch (error: any) {
      console.error('Failed to save print settings:', error);
      alert('❌ Gagal menyimpan: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoError('');

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setLogoError('❌ File harus berupa gambar (PNG, JPG, BMP)');
      return;
    }

    // Validate file size (max 500KB for thermal printer)
    if (file.size > 500 * 1024) {
      setLogoError('❌ Ukuran file maksimal 500KB');
      return;
    }

    // Read file and convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        // Validate image dimensions
        const maxWidth = settings.paperWidth === 58 ? 384 : 576; // pixels for thermal printer
        const maxHeight = 200;

        if (img.width > maxWidth || img.height > maxHeight) {
          setLogoError(`❌ Ukuran maksimal: ${maxWidth}x${maxHeight}px (saat ini: ${img.width}x${img.height}px)`);
          return;
        }

        // Convert to base64
        setSettings({ ...settings, logoBase64: event.target?.result as string, showLogo: true });
        setLogoError('');
      };
      img.onerror = () => {
        setLogoError('❌ Gagal memuat gambar');
      };
      img.src = event.target?.result as string;
    };
    reader.onerror = () => {
      setLogoError('❌ Gagal membaca file');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setSettings({ ...settings, logoBase64: undefined, showLogo: false });
    setLogoError('');
  };

  const handleConnectUSB = async () => {
    try {
      await thermalPrinter.requestPrinter();
      setPrinterConnected(true);
      setPrinterType('usb');
      alert('✅ Printer USB terhubung!');
    } catch (error: any) {
      alert('❌ Gagal terhubung: ' + error.message);
    }
  };

  const handleConnectBluetooth = async () => {
    try {
      await bluetoothPrinter.requestPrinter();
      setPrinterConnected(true);
      setPrinterType('bluetooth');
      alert('✅ Printer Bluetooth terhubung!');
    } catch (error: any) {
      alert('❌ Gagal terhubung: ' + error.message);
    }
  };

  const handleTestPrint = async () => {
    if (!printerConnected) {
      alert('⚠️ Hubungkan printer terlebih dahulu!');
      return;
    }

    setTestPrinting(true);
    try {
      const testReceipt = {
        invoiceNumber: 'TEST-001',
        date: new Date().toLocaleString('id-ID'),
        cashier: 'Test Cashier',
        customer: {
          name: 'Test Customer',
          phone: '081234567890',
          tier: 'Gold -10%',
        },
        items: [
          {
            name: 'LCD OPPO A1C/OPPO A1C/A16/A16E/...',
            quantity: 1,
            price: 140000,
            subtotal: 140000,
          },
          {
            name: 'LCD OPPO A5S A7 A12 A11K REPLACE',
            quantity: 1,
            price: 110000,
            subtotal: 110000,
          },
        ],
        subtotal: 250000,
        discount: 25000,
        total: 225000,
        paymentMethod: 'cash',
        paid: 250000,
        change: 25000,
        branchName: settings.headerText.split('\n')[0] || 'PhoneLabs',
        paperWidth: settings.paperWidth,
        continuousMode: settings.continuousMode,
        headerText: settings.headerText, // Use actual header from settings
        footerText: settings.footerText, // Use actual footer from settings
        logoBase64: settings.logoBase64, // Use actual logo from settings
      };

      if (printerType === 'usb') {
        await thermalPrinter.printReceipt(testReceipt as any);
      } else {
        await bluetoothPrinter.printReceipt(testReceipt as any);
      }

      alert('✅ Test print berhasil!');
    } catch (error: any) {
      alert('❌ Test print gagal: ' + error.message);
    } finally {
      setTestPrinting(false);
    }
  };

  const maxChars = settings.paperWidth === 58 ? 24 : 32;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <div className="text-xl">Loading settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/cashier')}
              className="text-gray-400 hover:text-white"
            >
              ← Kembali
            </button>
            <h1 className="text-2xl font-bold">⚙️ Pengaturan Print Nota</h1>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !branchId}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-semibold"
          >
            {saving ? '💾 Menyimpan...' : '💾 Simpan ke Database'}
          </button>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Settings Panel */}
          <div className="space-y-6">
            {/* Paper Width */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold mb-4">📏 Ukuran Kertas</h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setSettings({ ...settings, paperWidth: 58 })}
                  className={`p-4 rounded-lg border-2 transition-colors ${
                    settings.paperWidth === 58
                      ? 'border-blue-500 bg-blue-500/20'
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="text-2xl mb-2">📄</div>
                  <div className="font-semibold">58mm</div>
                  <div className="text-xs text-gray-400">Compact</div>
                </button>
                <button
                  onClick={() => setSettings({ ...settings, paperWidth: 80 })}
                  className={`p-4 rounded-lg border-2 transition-colors ${
                    settings.paperWidth === 80
                      ? 'border-blue-500 bg-blue-500/20'
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="text-2xl mb-2">📋</div>
                  <div className="font-semibold">80mm</div>
                  <div className="text-xs text-gray-400">Standard</div>
                </button>
              </div>
              <div className="mt-4 text-sm text-gray-400">
                Max {maxChars} karakter per baris
              </div>
            </div>

            {/* Print Mode */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold mb-4">✂️ Mode Pemotongan</h3>
              <div className="space-y-3">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    checked={settings.continuousMode}
                    onChange={() => setSettings({ ...settings, continuousMode: true })}
                    className="w-4 h-4 text-blue-500"
                  />
                  <div>
                    <div className="font-medium">Continuous (Rekomendasi)</div>
                    <div className="text-xs text-gray-400">
                      Tanpa auto-cut, sobek manual. Untuk kertas roll continuous.
                    </div>
                  </div>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    checked={!settings.continuousMode}
                    onChange={() => setSettings({ ...settings, continuousMode: false })}
                    className="w-4 h-4 text-blue-500"
                  />
                  <div>
                    <div className="font-medium">Auto-Cut</div>
                    <div className="text-xs text-gray-400">
                      Potong otomatis setiap nota. Untuk printer dengan cutter.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Header Text */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold mb-4">📝 Header Nota</h3>
              <textarea
                value={settings.headerText}
                onChange={(e) => setSettings({ ...settings, headerText: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white font-mono text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Nama Toko&#10;Alamat&#10;Telepon"
              />
              <div className="mt-2 text-xs text-gray-400">
                Gunakan Enter untuk baris baru. Max {maxChars} karakter per baris.
              </div>
            </div>

            {/* Logo Upload */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold mb-4">🖼️ Logo Toko</h3>
              
              {settings.logoBase64 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center p-4 bg-white rounded-lg">
                    <img 
                      src={settings.logoBase64} 
                      alt="Logo Toko" 
                      className="max-h-32 object-contain"
                    />
                  </div>
                  <button
                    onClick={handleRemoveLogo}
                    className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold"
                  >
                    🗑️ Hapus Logo
                  </button>
                </div>
              ) : (
                <div>
                  <label className="block">
                    <div className="w-full px-4 py-8 border-2 border-dashed border-gray-600 hover:border-blue-500 rounded-lg cursor-pointer transition-colors">
                      <div className="text-center">
                        <div className="text-4xl mb-2">📤</div>
                        <div className="font-semibold mb-1">Upload Logo</div>
                        <div className="text-sm text-gray-400">
                          Klik untuk pilih file
                        </div>
                      </div>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              )}

              {logoError && (
                <div className="mt-3 p-3 bg-red-500/20 border border-red-500 rounded-lg text-sm">
                  {logoError}
                </div>
              )}

              <div className="mt-4 p-4 bg-blue-500/20 border border-blue-500 rounded-lg">
                <div className="text-sm space-y-2">
                  <div className="font-semibold">ℹ️ Spesifikasi Logo:</div>
                  <ul className="space-y-1 text-gray-300 text-xs">
                    <li>• <strong>Format:</strong> PNG, JPG, BMP (PNG direkomendasikan)</li>
                    <li>• <strong>Ukuran 58mm:</strong> Max 384 x 200 pixels</li>
                    <li>• <strong>Ukuran 80mm:</strong> Max 576 x 200 pixels</li>
                    <li>• <strong>File size:</strong> Max 500KB</li>
                    <li>• <strong>Warna:</strong> Hitam putih/grayscale lebih bagus</li>
                    <li>• <strong>Background:</strong> Transparan lebih rapi</li>
                  </ul>
                  <div className="mt-2 pt-2 border-t border-blue-400/30 text-xs text-gray-400">
                    💡 Tip: Gunakan logo sederhana dengan kontras tinggi untuk hasil print terbaik
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Text */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold mb-4">📋 Footer Nota</h3>
              <textarea
                value={settings.footerText}
                onChange={(e) => setSettings({ ...settings, footerText: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white font-mono text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Terima Kasih&#10;Pesan custom Anda"
              />
            </div>

            {/* Printer Connection */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold mb-4">🖨️ Koneksi Printer</h3>
              
              {printerConnected ? (
                <div className="flex items-center gap-3 p-4 bg-green-500/20 border border-green-500 rounded-lg">
                  <div className="text-2xl">✅</div>
                  <div>
                    <div className="font-semibold">Printer Terhubung</div>
                    <div className="text-sm text-gray-400">
                      {printerType === 'usb' ? 'USB Thermal Printer' : 'Bluetooth Printer'}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <button
                    onClick={handleConnectUSB}
                    className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold flex items-center justify-center gap-2"
                  >
                    🔌 Hubungkan USB Printer
                  </button>
                  <button
                    onClick={handleConnectBluetooth}
                    className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold flex items-center justify-center gap-2"
                  >
                    📶 Hubungkan Bluetooth Printer
                  </button>
                </div>
              )}

              <button
                onClick={handleTestPrint}
                disabled={!printerConnected || testPrinting}
                className="w-full mt-3 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-semibold"
              >
                {testPrinting ? '⏳ Printing...' : '🖨️ Test Print'}
              </button>
            </div>
          </div>

          {/* Preview Panel */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold mb-4">👁️ Preview Nota</h3>
            
            <div className="bg-white text-black p-6 rounded-lg font-mono text-xs overflow-x-auto">
              {/* Logo */}
              {settings.logoBase64 && (
                <div className="text-center mb-3">
                  <img 
                    src={settings.logoBase64} 
                    alt="Logo" 
                    className="max-h-24 mx-auto object-contain"
                  />
                </div>
              )}
              
              {/* Header */}
              <div className="text-center mb-4">
                {settings.headerText.split('\n').map((line, i) => (
                  <div key={i} className={i === 0 ? 'font-bold text-lg' : ''}>
                    {line.substring(0, maxChars)}
                  </div>
                ))}
              </div>
              
              <div className="border-b-2 border-dashed border-black my-2"></div>
              
              {/* Transaction Info */}
              <div className="space-y-1">
                {settings.paperWidth === 58 ? (
                  <>
                    <div>TEST-001</div>
                    <div>{new Date().toLocaleDateString('id-ID')}</div>
                    <div>Kasir Test</div>
                    <div>Pelanggan</div>
                  </>
                ) : (
                  <>
                    <div>No: TEST-001</div>
                    <div>Tanggal: {new Date().toLocaleDateString('id-ID')}</div>
                    <div>Kasir: Kasir Test</div>
                    <div>Customer: Pelanggan</div>
                  </>
                )}
              </div>
              
              <div className="border-b-2 border-dashed border-black my-2"></div>
              
              {/* Items */}
              <div className="space-y-2">
                <div>
                  <div>LCD OPPO A1C...</div>
                  {settings.paperWidth === 58 ? (
                    <>
                      <div>1 x 140.000</div>
                      <div>= 140.000</div>
                    </>
                  ) : (
                    <div>  1 x 140.000 = 140.000</div>
                  )}
                </div>
                <div>
                  <div>LCD OPPO A5S A7...</div>
                  {settings.paperWidth === 58 ? (
                    <>
                      <div>1 x 110.000</div>
                      <div>= 110.000</div>
                    </>
                  ) : (
                    <div>  1 x 110.000 = 110.000</div>
                  )}
                </div>
              </div>
              
              <div className="border-b-2 border-dashed border-black my-2"></div>
              
              {/* Totals */}
              <div className="text-right space-y-1">
                {settings.paperWidth === 58 ? (
                  <>
                    <div>Sub: Rp 250.000</div>
                    <div>-10%: Rp 25.000</div>
                    <div className="font-bold text-lg">TOTAL: Rp 225.000</div>
                  </>
                ) : (
                  <>
                    <div>Subtotal: Rp 250.000</div>
                    <div>Diskon (-10%): Rp 25.000</div>
                    <div className="font-bold text-lg">TOTAL: Rp 225.000</div>
                  </>
                )}
              </div>
              
              <div className="border-b-2 border-dashed border-black my-2"></div>
              
              {/* Payment */}
              <div className="space-y-1">
                {settings.paperWidth === 58 ? (
                  <>
                    <div>TUNAI</div>
                    <div>Byr: Rp 250.000</div>
                    <div>Kbl: Rp 25.000</div>
                  </>
                ) : (
                  <>
                    <div>Metode: TUNAI</div>
                    <div>Bayar: Rp 250.000</div>
                    <div>Kembali: Rp 25.000</div>
                  </>
                )}
              </div>
              
              <div className="border-b-2 border-dashed border-black my-2"></div>
              
              {/* Footer */}
              <div className="text-center mt-4">
                {settings.footerText.split('\n').map((line, i) => (
                  <div key={i}>{line.substring(0, maxChars)}</div>
                ))}
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-500/20 border border-blue-500 rounded-lg">
              <div className="text-sm">
                <div className="font-semibold mb-2">💡 Tips:</div>
                <ul className="space-y-1 text-gray-300">
                  <li>• 58mm lebih compact, cocok untuk printer kecil</li>
                  <li>• 80mm lebih lebar, lebih mudah dibaca</li>
                  <li>• Logo akan dicetak di atas header (hitam-putih)</li>
                  <li>• Test print untuk lihat hasil asli</li>
                  <li>• Header & footer bisa multi-baris</li>
                  <li>• Ubah ukuran kertas = logo otomatis resize</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintSettingsPage;
